import { ArrowRight, CheckCircle2, Download } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { HomeCardItem, HomeStepItem } from "./types";
import { CardIcon, Kicker } from "./HomeShared";

interface HomeSectionsProps {
  capabilities: HomeCardItem[];
  latestVersion?: string;
  onDownload: () => void;
  services: HomeCardItem[];
  steps: HomeStepItem[];
}

export function HomeSections({ capabilities, latestVersion, onDownload, services, steps }: HomeSectionsProps) {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();

  return (
    <>
      <section id="capabilities" className="scroll-mt-[76px] bg-bg-surface py-[68px] sm:py-[72px]">
        <div className="mx-auto max-w-[1248px] px-5 sm:px-8 lg:px-0">
          <div className="grid gap-9 lg:grid-cols-[770px_380px] lg:items-end lg:justify-between">
            <div>
              <Kicker>{t("home.capabilitiesLabel")}</Kicker>
              <h2 className="mt-3 whitespace-pre-line font-display text-4xl font-bold leading-[1.06] tracking-normal text-text-primary sm:text-[52px]">
                {t("home.capabilitiesTitle")}
              </h2>
            </div>
            <div>
              <p className="text-[17px] leading-7 text-text-secondary">{t("home.capabilitiesDescription")}</p>
              <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-full bg-primary-soft px-4 py-2 font-mono text-[10px] font-semibold uppercase text-primary-ink">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-action-primary" />
                <span className="truncate">{t("home.capabilityTagDescription")}</span>
              </div>
            </div>
          </div>
          <div className="mt-[46px] grid overflow-hidden border border-border-subtle bg-bg-surface md:grid-cols-2 xl:grid-cols-4">
            {capabilities.map((item, index) => (
              <article key={item.title} className={`relative min-h-[300px] border-b border-border-subtle p-[22px] last:border-b-0 md:border-r md:[&:nth-child(2)]:border-r-0 xl:border-b-0 xl:[&:nth-child(2)]:border-r xl:last:border-r-0 ${index === 0 ? "bg-primary-soft" : "bg-bg-surface"}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-mono text-xs font-semibold ${index === 0 ? "text-primary-ink" : "text-text-muted"}`}>{String(index + 1).padStart(2, "0")}</span>
                  <CardIcon icon={item.icon} className={`${index === 0 ? "bg-action-primary text-white" : "bg-bg-elevated text-primary-ink"} h-[38px] w-[38px] rounded-md`} />
                </div>
                <div className="mt-32">
                  <h3 className="text-[21px] font-semibold leading-8 text-text-primary">{item.title}</h3>
                  <p className="mt-2 text-sm leading-[22px] text-text-secondary">{item.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="services" className="scroll-mt-[76px] bg-[var(--home-ink)] py-[72px] text-white">
        <div className="mx-auto grid max-w-[1248px] gap-12 px-5 sm:px-8 lg:grid-cols-[475px_1fr] lg:px-0">
          <div className="flex flex-col justify-between gap-12">
            <div>
              <Kicker className="text-action-primary-border" dotClassName="bg-action-primary-border">{t("home.servicesLabel")}</Kicker>
              <h2 className="mt-4 whitespace-pre-line font-display text-4xl font-bold leading-[1.18] tracking-normal text-white sm:text-[48px]">
                {t("home.servicesTitle")}
              </h2>
              <p className="mt-5 text-[17px] leading-7 text-[var(--home-footer-muted)]">{t("home.servicesDescription")}</p>
            </div>
            <div className="border border-[var(--home-ink-border)] bg-[var(--home-ink-elevated)] p-5">
              <div className="font-mono text-[10px] font-semibold uppercase text-white">{t("home.serviceStatusTitle")}</div>
              <p className="mt-1 text-[13px] leading-5 text-[var(--home-ink-muted)]">{t("home.serviceStatusDescription")}</p>
            </div>
          </div>
          <div className="overflow-hidden rounded-md border border-[var(--home-ink-border)] bg-[var(--home-ink-elevated)]">
            <div className="flex items-center justify-between gap-4 border-b border-[var(--home-ink-border)] px-5 py-5">
              <div className="flex items-center gap-3">
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-action-primary text-white"><CheckCircle2 className="h-4 w-4" /></div>
                <div className="font-mono text-[11px] font-semibold uppercase text-[var(--home-service-title)]">{t("home.serviceBoardTitle")}</div>
              </div>
              <span className="font-mono text-[10px] uppercase text-success">{latestVersion ? t("home.serviceBoardOnline", { count: services.length }) : t("home.releaseStatusPending")}</span>
            </div>
            <div>
              {services.map((item, index) => (
                <article key={item.title} className={`grid gap-4 border-b border-[var(--home-ink-border)] p-5 last:border-b-0 sm:grid-cols-[32px_38px_1fr_auto] sm:items-center ${index === 0 ? "bg-[var(--home-ink-panel)]" : "bg-[var(--home-ink-elevated)]"}`}>
                  <span className={`font-mono text-[10px] ${index === 0 ? "text-action-primary-border" : "text-[var(--home-service-muted)]"}`}>{String(index + 1).padStart(2, "0")}</span>
                  <CardIcon icon={item.icon} className={`${index === 0 ? "bg-action-primary text-white" : "bg-[var(--home-ink-border)] text-action-primary-border"} h-[38px] w-[38px] rounded-md`} />
                  <div>
                    <h3 className="text-lg font-semibold leading-7 text-white">{item.title}</h3>
                    <p className="mt-0.5 text-[13px] leading-5 text-[var(--home-ink-muted)]">{item.description}</p>
                  </div>
                  <span className={`font-mono text-[9px] font-semibold uppercase ${index === 0 ? "text-action-primary-border" : "text-[var(--home-service-muted)]"}`}>{t(`home.service${index + 1}Meta`)}</span>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="workflow" className="scroll-mt-[76px] bg-bg-page py-[70px]">
        <div className="mx-auto max-w-[1248px] px-5 sm:px-8 lg:px-0">
          <div className="grid gap-8 lg:grid-cols-[650px_420px] lg:items-end lg:justify-between">
            <div>
              <Kicker>{t("home.workflowLabel")}</Kicker>
              <h2 className="mt-3 whitespace-pre-line font-display text-4xl font-bold leading-[1.06] tracking-normal text-text-primary sm:text-[52px]">
                {t("home.workflowTitle")}
              </h2>
            </div>
            <div>
              <p className="text-[17px] leading-7 text-text-secondary">{t("home.workflowDescription")}</p>
              <div className="mt-3 font-mono text-[10px] font-semibold uppercase text-text-muted">{t("home.workflowMeta")}</div>
            </div>
          </div>
          <div className="mt-[54px] overflow-hidden rounded-md border border-border-subtle bg-bg-surface lg:grid lg:grid-cols-3">
            {steps.map((step, index) => (
              <article key={step.title} className={`relative min-h-[300px] border-b border-border-subtle p-[26px] last:border-b-0 lg:border-b-0 lg:border-r lg:last:border-r-0 ${index === 1 ? "bg-primary-soft" : "bg-bg-surface"}`}>
                <div className="flex items-center justify-between">
                  <span className={`flex h-10 w-10 items-center justify-center rounded-full ${index === 1 ? "bg-action-primary" : "bg-[var(--home-ink)]"} font-mono text-[11px] font-semibold text-white`}>{String(index + 1).padStart(2, "0")}</span>
                  <step.icon className="h-5 w-5 text-text-muted" />
                </div>
                <div className="mt-14">
                  <h3 className="text-[21px] font-semibold leading-8 text-text-primary">{step.title}</h3>
                  <p className="mt-2 text-sm leading-[23px] text-text-secondary">{step.description}</p>
                </div>
                <div className={`absolute bottom-[26px] left-[26px] font-mono text-[9px] font-semibold uppercase ${index === 1 ? "text-primary-ink" : "text-text-muted"}`}>{t(`home.step${index + 1}Meta`)}</div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-action-primary py-[58px] text-action-primary-text">
        <div className="mx-auto flex max-w-[1248px] flex-col items-start justify-between gap-8 px-5 sm:px-8 lg:flex-row lg:items-center lg:px-0">
          <div>
            <Kicker className="text-[var(--home-cta-soft)]" dotClassName="bg-[var(--home-cta-soft)]">{t("home.ctaKicker")}</Kicker>
            <h2 className="mt-4 font-display text-4xl font-bold leading-tight tracking-normal sm:text-[46px]">{t("home.ctaTitle")}</h2>
            <p className="mt-3 max-w-[525px] text-base leading-6 text-[var(--home-cta-text)]">{t("home.ctaDescription")}</p>
          </div>
          <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row">
            <button type="button" onClick={onDownload} className="flex h-12 items-center justify-center gap-2 rounded-md bg-bg-surface px-5 text-sm font-semibold text-primary-ink shadow-raised transition-colors hover:bg-bg-elevated">
              {t("home.downloadCta")}<Download className="h-4 w-4" />
            </button>
            <button type="button" onClick={() => { navigate("/register"); }} className="flex h-12 items-center justify-center gap-2 rounded-md border border-white/40 bg-white/10 px-5 text-sm font-semibold text-white transition-colors hover:bg-white/20">
              {t("home.primaryCta")}<ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
